import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Color, Colors } from '../styles/Colors';
import { MAX, v4 as uuidv4 } from 'uuid';
import ActionForm from './forms/ActionForm';
import ToggleForm from './ToggleForm';
import DocActionMenu from './DocActionMenu';
import { DocsWrapper, TableColumn } from '../styles/TableStyle';
import { FontScale } from '../styles/FontScale';
import { useTranslation } from 'react-i18next';

const AddFile = styled.div`
    margin: 1rem 0 0 0;

    & > div {
        margin: .5rem 0 0 0; 
        display: flex; 
        align-items: center; 
        background-color: ${Color(Colors.blue)};
        padding: 1.5rem 1rem 1.5rem 1rem; 
        border-radius: 4px; 
        width: 100%; 
        // min-width: 0; // Allow the element shrink within fit in the available parent width

        & > label {
            flex-shrink: 0; // Prevent label form shrinking when space is limited
            background-color: ${Color(Colors.white)};
            color: ${Color(Colors.blue)};
            padding: .5rem 1rem;
            border-radius: 4px; 
            cursor: pointer; 
            font-size: ${FontScale.size_1}; 
            
            &:hover {
                background-color: ${Color(Colors.lightBlue)};
                color: ${Color(Colors.white)};
            }
        }
            
        & > span {
            color: ${Color(Colors.white)};
            margin: 0 0 0 .5rem; 
        }

        & > input {
            display: none; 
        }
    }
`;

const IecAction = ({ docs, setDocs, plenaryScheduleImage, setPlenaryScheduleImage, type, addItemMsg, editItemMsg }) => {
    const { t } = useTranslation(); 

    const dynamicFields = [
        {
            type: 'text',
            label: t('common.formLabels.document.name'),
            name: 'document',
            placeholder: t('common.formLabels.document.label'),
            required: true,
        },
        {
            type: 'textarea',
            label: t('common.formLabels.description.name'),
            name: 'description',
            placeholder: t('common.formLabels.description.label'),
            required: true,
        },
    ];

    const [editItem, setEditItem] = useState({
        id: null,
        document: '',
        deadline: '',
        document_links: [{ fileName: '', url: '' }, { fileName: '', url: '' }],
        description: '',
        ...(type === 'correspondencen_docs' ? { vote: { answer: '', custom_answer: '', status: '' } } : { action: '', custom_action: '' })
    });
    
    const [newItem, setNewItem] = useState({
        id: null,
        document: '',
        deadline: '',
        document_links: [{ fileName: '', url: '' }, { fileName: '', url: '' }],
        description: '',
        ...(type === 'correspondencen_docs' ? { vote: { answer: '', custom_answer: '', status: '' } } : { action: '', custom_action: '' })
    });

      useEffect(() => {
        // Get items from local storage
        const storedItems = JSON.parse(localStorage.getItem(type)) || []

        if (storedItems.length > 0) {
            // Map through the current docs and replace with the stored version available
            setDocs((prevItems) => {
                const mergeItems = prevItems.map((doc) => {
                    const localStorageItems = storedItems.find((item) => item.id === doc.id)

                    // If an updated version exists and its different, return it to overwrite the original
                    if (localStorageItems && JSON.stringify(localStorageItems) !== JSON.stringify(doc)) {
                        return localStorageItems
                    }
                    return doc
                })
                
                // Get newly added items from local storage 
                const newItems = storedItems.filter((stored) => !prevItems.some((doc) => doc.id === stored.id))

                // Return the combined list: merged docs and new items from local storage
                return [...mergeItems, ...newItems]
            })
        }
      }, [type])

    const handleEditCancel = () => {
        setEditItem(prev => ({
            ...prev, 
            id: null
        }))
    }; 
      
    const handleEditSubmit = (event) => {
        event.preventDefault();
        const { document, description, deadline, document_links, action, custom_action, vote } = editItem;
    
        setDocs((prevItems) => {
            const items = prevItems.map((item) => {
                if (item.id === editItem.id) {
                    const updatedItem = {
                        ...item,
                        document,
                        description,
                        deadline,
                        document_links,
                        ...(type === 'correspondencen_docs' ? { vote } : { action, custom_action })
                    }

                    // Current list in local storage
                    const getItem = JSON.parse(localStorage.getItem(type)) || [];

                    // Check if this task was already edited
                    const existingIndex = getItem.findIndex(i => i.id === updatedItem.id); 

                    if (existingIndex !== -1) {
                        // Replace the item if it exist
                        getItem[existingIndex] = updatedItem; 
                    } else {
                        // Otherwise save it to the list
                        getItem.push(updatedItem)
                    }

                    localStorage.setItem(type, JSON.stringify(getItem))

                    return updatedItem
                }
                return item
            });
    
            return items;
        });
    
        setEditItem({
            id: null,
            document: '',
            deadline: '',
            document_links: [{ fileName: '', url: '' }, { fileName: '', url: '' }],
            description: '',
            ...(type === 'correspondencen_docs' ? { vote: { answer: '', custom_answer: '', status: '' } } : { action: '', custom_action: '' })
        });
    };
    
    const handleNewSubmit = (event) => {
        event.preventDefault();
        const { document, description, deadline, document_links, action, custom_action, vote } = newItem;

        const newDoc = {
            id: uuidv4(),
            document,
            description,
            deadline,
            document_links,
            ...(type === 'correspondencen_docs' ? { vote } : { action, custom_action })
        }
    
        setDocs((prevItems) => {
            const addNewDoc = [...prevItems, newDoc]; 
            const storedItems = JSON.parse(localStorage.getItem(type)) || [];
            const updatedStoredDocs = [...storedItems, newDoc]

            localStorage.setItem(type, JSON.stringify(updatedStoredDocs))

            return addNewDoc

        });
    
        setNewItem({
            id: null,
            document: '',
            deadline: '',
            document_links: [{ fileName: '', url: '' }, { fileName: '', url: '' }],
            description: '',
            ...(type === 'correspondencen_docs' ? { vote: { answer: '', custom_answer: '', status: '' } } : { action: '', custom_action: '' })
        });
    };
    
    const handleDeleteText = (id) => {
        const deleteItem = docs.filter((item) => item.id !== id);
        setDocs(deleteItem);
        localStorage.setItem(type, JSON.stringify(deleteItem));
    };
    
    const handleEditTextClick = (item) => {
        setEditItem({
            id: item.id,
            document: item.document,
            description: item.description,
            deadline: item.deadline,
            document_links: item.document_links,
            ...(type === 'correspondencen_docs' ? { vote: item.vote } : { action: item.action, custom_action: item.custom_action })
        });
    };
    
    const handleEditChange = (event) => {
        const { name, value } = event.target;
        if (name.startsWith('document_links')) {
            const index = parseInt(name.split('.')[1], 10);
            const updatedLinks = [...editItem.document_links];
            const property = name.split('.')[2];
            updatedLinks[index] = { ...updatedLinks[index], [property]: value };
            setEditItem({ ...editItem, document_links: updatedLinks });
        } else if (name === 'action' && value !== t('actionDocs.tableHeadTitles.customAction')) {
            setEditItem({ ...editItem, action: value, custom_action: '' });
        } else if (name === 'vote.answer' && value !== t('actionDocs.tableHeadTitles.customAction')) {
            setEditItem({ ...editItem, vote: { answer: value, custom_answer: '' }});
        } else if (name.startsWith('vote')) {
            const property = name.split('.')[1];
            setEditItem({ ...editItem, vote: { ...editItem.vote, [property]: value } });
        } else {
            setEditItem({ ...editItem, [name]: value });
        }
    };
    
    const handleNewChange = (event) => {
        const { name, value } = event.target;
        if (name.startsWith('document_links')) {
            const index = parseInt(name.split('.')[1], 10);
            const updatedLinks = [...newItem.document_links];
            const property = name.split('.')[2];
            updatedLinks[index] = { ...updatedLinks[index], [property]: value };
            setNewItem({ ...newItem, document_links: updatedLinks });
        } else if (name === 'action' && value !== t('actionDocs.tableHeadTitles.customAction')) {
            setNewItem({ ...newItem, action: value, custom_action: '' });
        } else if (name === 'vote.answer' && value !== t('actionDocs.tableHeadTitles.customAction')) {
            setNewItem({ ...newItem, vote: {answer: value, custom_answer: ''} });
        } else if (name.startsWith('vote')) {
            const property = name.split('.')[1];
            setNewItem({ ...newItem, vote: { ...newItem.vote, [property]: value } });
        } else {
            setNewItem({ ...newItem, [name]: value });
        }
    };


    const showPlenaryScheduleImage = () => {
        const handlePlenaryImage = (event) => {
            const image = event.target.files[0];

            if (!image) return;

            if (!image.type.startsWith('image/')) {
                setPlenaryScheduleImage(null);
                event.target.value = '';
                return;
            }

            const reader = new FileReader();

            reader.onload = () => {
                const savedImage = {
                    lastModified: image.lastModified,
                    lastModifiedDate: image.lastModifiedDate, 
                    name: image.name,
                    size: image.size,
                    type: image.type,
                    webkitRelativePath: image.webkitRelativePath,
                    data: reader.result
                };

                localStorage.setItem(
                    'plenary_schedule_image',
                    JSON.stringify(savedImage)
                );

                setPlenaryScheduleImage(savedImage);
            };

            reader.readAsDataURL(image);
        }; 

        const getDADocs = docs.filter((item) => item.document && item.document.includes('/DA'));
        
        if ((getDADocs && getDADocs.length > 0) && (type === 'action_docs')) {
            const MAX_FILE_NAME_LENGTH = 105; 
            const fileName = plenaryScheduleImage?.name;
            const slicedFileName = fileName && fileName.length > MAX_FILE_NAME_LENGTH
                ? `${fileName.slice(0, MAX_FILE_NAME_LENGTH)}...`
                : fileName
            return (
                <AddFile>
                    <h5>{`${t('common.addBtnLabel')} ${t('actionDocs.formLabels.plenaryMeeting.title')}`}</h5>
                    <div>
                        <label htmlFor='plenary_schedule_image'>{t('actionDocs.formLabels.plenaryMeeting.buttonLabel')}</label>
                        <span>
                            {fileName 
                                ? slicedFileName
                                : t('actionDocs.formLabels.plenaryMeeting.placeHolder')
                            }
                        </span>
                        <input
                            type='file'
                            name='plenary_schedule_image'
                            id='plenary_schedule_image'
                            accept='image/*'
                            onChange={handlePlenaryImage}
                            hidden
                        />
                    </div>
                </AddFile>
            )
        }
    }

    return (
        <div>
            <div>
                {docs && docs.length > 0 ? (
                    <DocsWrapper type='action_documents'>
                        <div>
                            <span>{t('common.formLabels.document.name')}</span>
                            <span>{t('actionDocs.tableHeadTitles.deadline.name')}</span>
                            <span>{t('actionDocs.tableHeadTitles.action.name')}</span>
                            <span></span>
                        </div>

                        {docs.map((item, index) => (
                            <div key={item.id}>
                                {editItem.id === item.id ? (
                                    <ActionForm
                                        item={editItem}
                                        setItem={setEditItem}
                                        handleOnChange={handleEditChange}
                                        handleSubmit={handleEditSubmit}
                                        handleEditCancel={handleEditCancel}
                                        title={`${editItemMsg} #${index + 1}`}
                                        index={index}
                                        docs={docs.length}
                                        type='edit_task'
                                        dynamicFields={dynamicFields}
                                        buttonColorTheme='lightBlue'
                                        docType={type}

                                    />
                                ) : (
                                    <>
                                        <TableColumn>
                                            <p>{item.document}</p>
                                            <div>
                                                {item.document_links && item.document_links.map((link, index) => (
                                                    <a key={index} href={link.url} target='_blank' rel='noopener noreferrer'>{link.fileName}</a>
                                                ))}
                                            </div>
                                            <p>{item.description}</p>
                                        </TableColumn>
                                        <span>{new Date(item?.deadline).toLocaleDateString()}</span>
                                        {type === 'correspondencen_docs' ? (
                                            <span>{item.vote.custom_answer ? item.vote.custom_answer : item.vote.answer}</span>
                                        ) : (
                                            <span>{item.custom_action ? item.custom_action : item.action}</span>
                                        )}
                                        <span>
                                            <DocActionMenu
                                                item={item}
                                                handleDeleteText={handleDeleteText}
                                                handleEditTextClick={handleEditTextClick}
                                            />
                                        </span>
                                    </>
                                )}
                            </div>
                        ))}
                    </DocsWrapper>
                ) : (
                    <span>{t('common.noDocuments')}</span>
                )}
            </div>
            <ToggleForm>
                <ActionForm
                    item={newItem}
                    setItem={setNewItem}
                    handleOnChange={handleNewChange}
                    handleSubmit={handleNewSubmit}
                    title={addItemMsg}
                    type='new_task'
                    dynamicFields={dynamicFields}
                    buttonColorTheme='darkBlue'
                    docType={type}
                />
            </ToggleForm>
            {showPlenaryScheduleImage()}
        </div>
    )
}

export default IecAction